import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Form, Icon, Modal } from "semantic-ui-react";

import { DATE_FORMATS_FNS, useCan, useSelectedLocation } from "components";
import { typedQueryClient, usePermanentRealTimeUser } from "hooks";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES, APPOINTMENT_RECALL_STATUSES, CUSTOMER_TYPES, PIN_TYPES } from "models";
import { CarMileageModal } from "modules/AppointmentDetails/components";
import {
  AppointmentFormSection,
  CarFormSection,
  CarSearchFormSection,
  CustomerSearchFormSection,
  CustomerType,
  InterventionsFormSection
} from "modules/Appointments/components/NewAppointment/components";
import { useNewAppointmentMutation, useWoNrQuery } from "modules/Appointments/components/NewAppointment/hooks";
import "modules/Appointments/components/NewAppointment/NewAppointment.scss";
import { FIXED_VIN_LENGTH, generateRandomString, hasAnyFieldValue, isEmptyStringField } from "modules/Appointments/components/NewAppointment/utils";
import { AppointmentsQueryKeys } from "modules/Appointments/queryKeys";
import { ApiInstance } from "utils/Api";
import { canWriteBackCustomerData, generateDMSNr } from "utils/appointmentUtils";
import { isKeyLoopLocation } from "utils/common";
import { formatCurrentUTCDate, formatDate, toISOString } from "utils/dateHelperFunctions";

import type { AddInterventionAndAppendPinPayloads } from "components";
import type { Appointment, Car, Customer, Intervention } from "models";
import type { CreateNewAppointmentPayload } from "modules/Appointments/components/NewAppointment/hooks";

export type CustomerSection = Omit<
  Customer,
  "tel_mobile_private_invalid_for_whats_app" | "tel_mobile_business_invalid_for_whats_app" | "dealer_location" | "dealer" | "appointments" | "cars" | "notes"
>;

export type CarSection = Pick<Car, "id" | "make" | "model" | "apk_date_dms" | "dms_nr" | "reg_nr" | "vin_nr">;

export type AppointmentSection = Pick<
  Appointment,
  "wo_nr" | "internal" | "is_lease_company" | "customer_waiting" | "dealer_location_id" | "car_return_time" | "has_recalls_data"
> & {
  time_car_app: Date;
  due_in: Date | null;
  car_in_shop: boolean;
  car: CarSection;
  customer_owner: CustomerSection | null;
  customer_driver: CustomerSection | null;
  customer_contract: CustomerSection | null;
};

export type HandleCustomerResultSelectParams = {
  customer?: Customer;
  customerType?: CUSTOMER_TYPES;
};

type HandleSetCustomerParams = {
  type: "customer_driver" | "customer_owner" | "customer_contract" | "not_set";
  customer: CustomerSection;
};

export const CUSTOMER_DRIVER_FIELDS: (keyof CustomerSection)[] = [
  "dms_nr",
  "firstname",
  "surname",
  "street",
  "postcode",
  "place",
  "email_business",
  "tel_mobile_business",
  "tel_business_nr"
];
export const CUSTOMER_OWNER_CONTRACTOR_FIELDS: (keyof CustomerSection)[] = [...CUSTOMER_DRIVER_FIELDS, "company"];

export const customerTypeMap = {
  [CUSTOMER_TYPES.NotSet]: "not_set",
  [CUSTOMER_TYPES.Driver]: "customer_driver",
  [CUSTOMER_TYPES.Owner]: "customer_owner",
  [CUSTOMER_TYPES.Contractor]: "customer_contract"
} as const;

export enum SEARCH_TAGS {
  DMS = "DMS",
  Claire = "Claire"
}

const MAX_NUMBER_OF_WO_ERROR = "maximum WONr reached";

const FormHelper = createFormHelper<AppointmentSection>();

export const CreateAppointment = () => {
  const { t } = useTranslation();

  const { user } = usePermanentRealTimeUser();
  const { selectedLocation } = useSelectedLocation();
  const canCreateFullPin = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.PinFull);

  const [hasFormErrors, setHasFormErrors] = useState(false);
  const [showCreateAppointmentModal, setShowCreateAppointmentModal] = useState(false);
  const [activeCustomerTab, setActiveCustomerTab] = useState<CUSTOMER_TYPES>(CUSTOMER_TYPES.Driver);
  const [interventionsAndPins, setInterventionsAndPins] = useState<AddInterventionAndAppendPinPayloads[]>([]);

  const [isCustomerOwnerPreselected, setIsCustomerOwnerPreselected] = useState(false);
  const [isCustomerDriverPreselected, setIsCustomerDriverPreselected] = useState(false);
  const [isCustomerContractorPreselected, setIsCustomerContractorPreselected] = useState(false);

  const [isDisabledOwnerSearch, setIsDisabledOwnerSearch] = useState(false);
  const [isDisabledDriverSearch, setIsDisabledDriverSearch] = useState(false);
  const [isDisabledContractorSearch, setIsDisabledContractorSearch] = useState(false);
  const { refetch: refetchWoNr, data: appointmentWoNr, isSuccess: isGetWoNrSuccess, error: getWoNrError } = useWoNrQuery();

  const [carMileage, setCarMileage] = useState(0);
  const [carSearchTerm, setCarSearchTerm] = useState("");
  const [lastSelectedTitle, setLastSelectedTitle] = useState("");
  const [carFromExternalData, setCarFromExternalData] = useState(false);
  const [showCarMileageModal, setShowCarMileageModal] = useState(false);
  const [showCarTag, setShowCarTag] = useState<SEARCH_TAGS | null>(null);

  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [showCustomerType, setShowCustomerType] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null | undefined>(null);

  const { mutateAsync: createAppointment, isPending: isCreateAppointmentPending } = useNewAppointmentMutation();

  useEffect(() => {
    if (isGetWoNrSuccess) {
      setShowCreateAppointmentModal(true);
      return;
    }

    if (getWoNrError) {
      if (getWoNrError.message === MAX_NUMBER_OF_WO_ERROR) toast.error(t("v8_wo_limit_reached", "Work Order Limit Reached"));
      else toast.error(getWoNrError.message);
    }
  }, [isGetWoNrSuccess, getWoNrError]);

  const hasWriteBackPermissionForCustomerData = canWriteBackCustomerData(selectedLocation);
  const keyloopLocation = isKeyLoopLocation(selectedLocation);

  const appointmentFormSection = FormHelper.useForm({
    mode: "all",
    defaultValues: {
      wo_nr: "",
      internal: false,
      car_in_shop: false,
      customer_waiting: false,
      is_lease_company: false,
      due_in: new Date(),
      time_car_app: new Date(),
      dealer_location_id: selectedLocation?.id ?? -1,
      has_recalls_data: APPOINTMENT_RECALL_STATUSES.None,
      car_return_time: new Date(Date.now() + 60 * 1000).toISOString(),
      car: { id: -1, apk_date_dms: null, reg_nr: "", make: "", model: "", vin_nr: "", dms_nr: "" },
      customer_contract: null,
      customer_driver: null,
      customer_owner: null
    }
  });

  const handleResetCarMileageModal = () => {
    appointmentFormSection.setValue("car_in_shop", false);
    setShowCarMileageModal(false);
    setCarMileage(0);
  };

  const handleCustomerTabChange = (customerType: CUSTOMER_TYPES) => setActiveCustomerTab(customerType);
  const handleSetCustomer = ({ customer, type }: HandleSetCustomerParams) => {
    if (type === "not_set") return;
    appointmentFormSection.setValue(type, customer);
  };

  const handleCustomerResultSelect = ({ customer, customerType }: HandleCustomerResultSelectParams) => {
    if (!customer) {
      if (customerType) appointmentFormSection.setValue(customerTypeMap[customerType], null);
      return;
    }

    if (customer.customer_type === CUSTOMER_TYPES.NotSet) {
      if (activeCustomerTab === CUSTOMER_TYPES.Driver) handleSetCustomer({ customer, type: "customer_driver" });
      else if (activeCustomerTab === CUSTOMER_TYPES.Owner) handleSetCustomer({ customer, type: "customer_owner" });
      else if (activeCustomerTab === CUSTOMER_TYPES.Contractor) handleSetCustomer({ customer, type: "customer_contract" });
      return;
    }

    handleSetCustomer({ customer, type: customerTypeMap[customer.customer_type] });
  };

  const handlePinInterventions = async (addedInterventions: Intervention[], interventionsAndPins: AddInterventionAndAppendPinPayloads[]) => {
    if (!interventionsAndPins.length || !addedInterventions.length) return;

    for (const interventionAndPin of interventionsAndPins) {
      const addedIntervention = addedInterventions.find(i => i.reference_nr === interventionAndPin.intervention.reference_nr);
      if (!addedIntervention) continue;

      let pinPayload = null;

      if (interventionAndPin.pin) {
        pinPayload = interventionAndPin.pin;
        pinPayload.intervention_id = addedIntervention.id;
        pinPayload.appointment_id = addedIntervention.appointment_id;
      } else if (interventionAndPin.intervention.rdw_recall_id || interventionAndPin.intervention.pon_recall_id || interventionAndPin.intervention.recalls_id) {
        pinPayload = {
          pin_type_id: PIN_TYPES.Recall,
          intervention_id: addedIntervention.id,
          appointment_id: addedIntervention.appointment_id,
          user_id: user?.id ?? -1,
          rdw_recall_id: interventionAndPin.intervention.rdw_recall_id,
          pon_recall_id: interventionAndPin.intervention.pon_recall_id,
          recalls_id: interventionAndPin.intervention.recalls_id,
          created_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat)
        };
      }

      if (pinPayload && canCreateFullPin) await ApiInstance.post("/pins/append", pinPayload);
    }
  };

  const handleCloseCreateAppointmentModal = () => {
    setShowCreateAppointmentModal(false);
    appointmentFormSection.reset();
    setHasFormErrors(false);
    setActiveCustomerTab(CUSTOMER_TYPES.Driver);
    setInterventionsAndPins([]);
    setIsCustomerOwnerPreselected(false);
    setIsCustomerDriverPreselected(false);
    setIsCustomerContractorPreselected(false);
    setIsDisabledOwnerSearch(false);
    setIsDisabledDriverSearch(false);
    setIsDisabledContractorSearch(false);
    setCarMileage(0);
    setCarSearchTerm("");
    setLastSelectedTitle("");
    setCarFromExternalData(false);
    setShowCarMileageModal(false);
    setShowCarTag(null);
    setCustomerSearchTerm("");
    setShowCustomerType(null);
    setSelectedCustomer(null);
    typedQueryClient.resetQueries(AppointmentsQueryKeys.searchCars);
    typedQueryClient.resetQueries(AppointmentsQueryKeys.woNumber);
  };

  const checkIsFormInvalid = () => {
    let hasError = false;
    let firstErrorTab: CUSTOMER_TYPES | null = null;
    const appointmentValues = appointmentFormSection.getValues();

    if (isEmptyStringField(appointmentValues.car.make)) hasError = true;
    else if (!appointmentValues.car.model || appointmentValues.car.model.trim() === "") hasError = true;
    else if (
      appointmentValues.customer_driver &&
      hasAnyFieldValue(appointmentValues.customer_driver, CUSTOMER_DRIVER_FIELDS) &&
      isEmptyStringField(appointmentValues.customer_driver.surname)
    ) {
      hasError = true;
      firstErrorTab = CUSTOMER_TYPES.Driver;
    } else if (
      appointmentValues.customer_owner &&
      hasAnyFieldValue(appointmentValues.customer_owner, CUSTOMER_OWNER_CONTRACTOR_FIELDS) &&
      isEmptyStringField(appointmentValues.customer_owner.surname) &&
      isEmptyStringField(appointmentValues.customer_owner.company)
    ) {
      hasError = true;
      firstErrorTab = CUSTOMER_TYPES.Owner;
    } else if (
      appointmentValues.customer_contract &&
      hasAnyFieldValue(appointmentValues.customer_contract, CUSTOMER_OWNER_CONTRACTOR_FIELDS) &&
      isEmptyStringField(appointmentValues.customer_contract.surname) &&
      isEmptyStringField(appointmentValues.customer_contract.company)
    ) {
      hasError = true;
      firstErrorTab = CUSTOMER_TYPES.Contractor;
    } else if (!hasWriteBackPermissionForCustomerData && isEmptyStringField(appointmentValues.wo_nr)) hasError = true;
    else if (!appointmentValues.car_return_time) hasError = true;
    else if (keyloopLocation && (!appointmentValues.car.vin_nr || appointmentValues.car.vin_nr.trim().length !== FIXED_VIN_LENGTH)) hasError = true;
    else if (!keyloopLocation && isEmptyStringField(appointmentValues.car.reg_nr)) hasError = true;

    if (hasError) {
      setHasFormErrors(true);
      if (firstErrorTab) setActiveCustomerTab(firstErrorTab);

      return true;
    }

    return false;
  };

  const handleSubmitForm = async () => {
    if (checkIsFormInvalid()) return;

    const appointmentValues = appointmentFormSection.getValues();
    const dueInDate = formatDate(appointmentValues.time_car_app, DATE_FORMATS_FNS.yearMonthDateDash);
    const dueInTime = formatDate(appointmentValues.due_in ?? new Date(), DATE_FORMATS_FNS.hoursMinutes);

    const isCustomerOwnerValid = appointmentValues.customer_owner && Object.values(appointmentValues.customer_owner).some(v => v !== null);
    const isCustomerDriverValid = appointmentValues.customer_driver && Object.values(appointmentValues.customer_driver).some(v => v !== null);
    const isCustomerContractorValid = appointmentValues.customer_contract && Object.values(appointmentValues.customer_contract).some(v => v !== null);

    const appointmentToSubmit: CreateNewAppointmentPayload = {
      ...appointmentValues,
      dms_nr: generateDMSNr(),
      time_car_app: toISOString(appointmentValues.time_car_app),
      due_in: toISOString(dueInDate + " " + dueInTime),
      car_return_time: toISOString(appointmentValues.car_return_time),
      car_in_shop: appointmentValues.car_in_shop ? new Date().toISOString() : null,
      customer_owner: isCustomerOwnerValid ? appointmentValues.customer_owner : null,
      customer_driver: isCustomerDriverValid ? appointmentValues.customer_driver : null,
      customer_contract: isCustomerContractorValid ? appointmentValues.customer_contract : null,
      interventions: interventionsAndPins.map(ip => ip.intervention)
    };

    if (!appointmentValues.car.vin_nr)
      appointmentToSubmit.car = { ...appointmentValues.car, dms_nr: appointmentValues.car.dms_nr || `claire_${toISOString(new Date())}_${generateRandomString(10)}` };

    if (selectedLocation?.id) appointmentToSubmit.dealer_location_id = selectedLocation.id;

    if (!hasWriteBackPermissionForCustomerData) {
      const now = toISOString(new Date());
      if (appointmentToSubmit.customer_driver && !appointmentToSubmit.customer_driver.dms_nr) appointmentToSubmit.customer_driver.dms_nr = `claire_driver_${now}`;
      if (appointmentToSubmit.customer_owner && !appointmentToSubmit.customer_owner.dms_nr) appointmentToSubmit.customer_owner.dms_nr = `claire_owner_${now}`;
      if (appointmentToSubmit.customer_contract && !appointmentToSubmit.customer_contract.dms_nr)
        appointmentToSubmit.customer_contract.dms_nr = `claire_contractor_${now}`;
    }

    if (!appointmentToSubmit.car) return toast.error(t("v8_missing_car", "Missing car"));

    if (!appointmentToSubmit.customer_driver && !appointmentToSubmit.customer_owner && !appointmentToSubmit.customer_contract)
      return toast.error(t("v8_missing_customer", "Missing customer"));

    if (hasWriteBackPermissionForCustomerData && appointmentToSubmit.car_in_shop) appointmentToSubmit.car_in_shop = null;

    const { appointment } = await createAppointment(appointmentToSubmit);

    if (appointment.interventions?.length) await handlePinInterventions(appointment.interventions, interventionsAndPins);

    if (appointmentValues.car_in_shop && hasWriteBackPermissionForCustomerData)
      ApiInstance.post("/appointments/set_car_in_shop", { appointment_id: appointment.id, current_km: carMileage });

    handleCloseCreateAppointmentModal();
  };

  return (
    <>
      <div className="NewAppointment">
        <Button icon basic onClick={() => refetchWoNr()}>
          <Icon className="plus large" />
        </Button>
        <Modal open={showCreateAppointmentModal} className="NewAppointment" size="fullscreen">
          <Modal.Header>{t("v8_create_workorder", "Create Workorder")}</Modal.Header>
          <Modal.Content className={"NewAppointment__ModalContent"}>
            <Form className="NewAppointment__Form">
              <FormHelper.FormProvider {...appointmentFormSection}>
                <CarSearchFormSection
                  showCarTag={showCarTag}
                  carSearchTerm={carSearchTerm}
                  lastSelectedTitle={lastSelectedTitle}
                  handleOpenCarMileageModal={() => setShowCarMileageModal(true)}
                  handleSetCustomerOwnerPreselect={(value: boolean) => setIsCustomerOwnerPreselected(value)}
                  handleSetCustomerDriverPreselect={(value: boolean) => setIsCustomerDriverPreselected(value)}
                  handleSetCustomerContractorPreselect={(value: boolean) => setIsCustomerContractorPreselected(value)}
                  handleSetDisabledOwnerSearch={(value: boolean) => setIsDisabledOwnerSearch(value)}
                  handleSetDisabledDriverSearch={(value: boolean) => setIsDisabledDriverSearch(value)}
                  handleSetDisabledContractorSearch={(value: boolean) => setIsDisabledContractorSearch(value)}
                  handleSetShowCarTag={(value: SEARCH_TAGS | null) => setShowCarTag(value)}
                  handleSetCarSearchTerm={(value: string) => setCarSearchTerm(value)}
                  handleSetLastSelectedTitle={(value: string) => setLastSelectedTitle(value)}
                  handleCustomerResultSelect={handleCustomerResultSelect}
                  handleSetCarFromExternalData={(value: boolean) => setCarFromExternalData(value)}
                />
                <CarFormSection hasErrors={hasFormErrors} carFromExternalData={carFromExternalData} lastSelectedTitle={lastSelectedTitle} />
                <CustomerSearchFormSection
                  showCarTag={showCarTag}
                  isDisabledContractorSearch={isDisabledContractorSearch}
                  isDisabledDriverSearch={isDisabledDriverSearch}
                  isDisabledOwnerSearch={isDisabledOwnerSearch}
                  activeCustomerTab={activeCustomerTab}
                  customerSearchTerm={customerSearchTerm}
                  showCustomerType={showCustomerType}
                  selectedCustomer={selectedCustomer}
                  handleSetCustomerSearchTerm={setCustomerSearchTerm}
                  handleSetShowCustomerType={setShowCustomerType}
                  handleSetSelectedCustomer={setSelectedCustomer}
                  handleCustomerTabChange={handleCustomerTabChange}
                  handleCustomerResultSelect={handleCustomerResultSelect}
                />
                <CustomerType
                  activeTab={activeCustomerTab}
                  hasValidationError={hasFormErrors}
                  handleCustomerTabChange={handleCustomerTabChange}
                  isCustomerDriverPreselected={isCustomerDriverPreselected}
                  isCustomerOwnerPreselected={isCustomerOwnerPreselected}
                  isCustomerContractorPreselected={isCustomerContractorPreselected}
                />
                <AppointmentFormSection hasErrors={hasFormErrors} appointmentWoNr={appointmentWoNr} isGetWoNrSuccess={isGetWoNrSuccess} />
                <InterventionsFormSection interventionsAndPins={interventionsAndPins} setInterventionsAndPins={setInterventionsAndPins} />
              </FormHelper.FormProvider>
            </Form>
          </Modal.Content>
          <Modal.Actions className="padding-container">
            <div className="action-buttons">
              <Button className="light" onClick={handleCloseCreateAppointmentModal}>
                {t("v8_cancel", "Cancel")}
                <Icon className="xmark" />
              </Button>
              <Button type="submit" color="green" onClick={handleSubmitForm} loading={isCreateAppointmentPending} disabled={isCreateAppointmentPending}>
                {t("v8_save", "Save")}
                <Icon className="check" />
              </Button>
            </div>
          </Modal.Actions>
        </Modal>
      </div>
      <CarMileageModal
        currentMileage={carMileage}
        showCarMileageModal={showCarMileageModal}
        title={t("v8_car_in_shop", "Car in Shop")}
        onResetCarMileageModal={handleResetCarMileageModal}
        onSetCurrentMileage={value => setCarMileage(value)}
        onHandleCarMileageConfirm={() => setShowCarMileageModal(false)}
      />
    </>
  );
};
